import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n/useI18n';

const _clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const _formatIsoDate = (d) => d.toISOString().slice(0, 10);

const _getYtdStart = () => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

const _getDateDaysAgo = (days) => {
  const now = new Date();
  const next = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return _formatIsoDate(next);
};

const _buildLinePath = (points, width, height, padding) => {
  if (!points.length) return '';
  const values = points.map((p) => Number(p.market_value_usd || 0));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const span = Math.max(maxValue - minValue, 0.0001);
  const xSpan = Math.max(points.length - 1, 1);
  const usableWidth = Math.max(width - padding * 2, 1);
  const usableHeight = Math.max(height - padding * 2, 1);

  return points
    .map((p, index) => {
      const x = padding + (index / xSpan) * usableWidth;
      const normalized = (maxValue - Number(p.market_value_usd || 0)) / span;
      const y = padding + normalized * usableHeight;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const _polarToCartesian = (cx, cy, r, angle) => ({
  x: cx + r * Math.cos(angle),
  y: cy + r * Math.sin(angle)
});

const _describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = _polarToCartesian(cx, cy, r, startAngle);
  const end = _polarToCartesian(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)} Z`;
};

export const OverviewPanel = ({
  isMobileViewport,
  livePulse,
  overviewPositionFilter,
  setOverviewPositionFilter,
  positionFilters,
  summaryCards,
  overviewPositionLabel,
  scopedMarketValueUsd,
  scopedDividendUsd,
  scopedRealizedProfitUsd,
  scopedUnrealizedProfitUsd,
  scopedAssetCount,
  scopedAssets,
  returnRankAssets,
  apiFetch,
  formatNumber,
  formatSigned,
  getPulseValueDigits,
  onOpenAsset,
}) => {
  const { t } = useI18n();
  const [trendRangeKey, setTrendRangeKey] = useState('YTD');
  const [trend, setTrend] = useState({ points: [], dividends: [], start_date: '', end_date: '' });
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState('');

  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);

  const trendRange = useMemo(() => {
    const endDate = _formatIsoDate(new Date());
    if (trendRangeKey === '30D') {
      return { startDate: _getDateDaysAgo(30), endDate, stepDays: 2 };
    }
    if (trendRangeKey === '90D') {
      return { startDate: _getDateDaysAgo(90), endDate, stepDays: 7 };
    }
    if (trendRangeKey === '1Y') {
      return { startDate: _getDateDaysAgo(365), endDate, stepDays: 14 };
    }
    return { startDate: _getYtdStart(), endDate, stepDays: 7 };
  }, [trendRangeKey]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setTrendLoading(true);
      setTrendError('');
      try {
        const query = new URLSearchParams();
        query.set('start_date', trendRange.startDate);
        query.set('end_date', trendRange.endDate);
        query.set('step_days', String(trendRange.stepDays));
        const response = await apiFetch(`/dashboard/trend?${query.toString()}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || t('overview.trend.loadFailed'));
        }
        if (!cancelled) {
          setTrend({
            points: Array.isArray(data.points) ? data.points : [],
            dividends: Array.isArray(data.dividends) ? data.dividends : [],
            start_date: data.start_date || trendRange.startDate,
            end_date: data.end_date || trendRange.endDate,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setTrend({ points: [], dividends: [], start_date: '', end_date: '' });
          setTrendError(error.message || t('overview.trend.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setTrendLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, t, trendRange]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setRecentLoading(true);
      setRecentError('');
      try {
        const response = await apiFetch('/dashboard/recent-transactions?limit=10');
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || t('overview.recent.loadFailed'));
        }
        if (!cancelled) {
          setRecentTransactions(Array.isArray(data.transactions) ? data.transactions : []);
        }
      } catch (error) {
        if (!cancelled) {
          setRecentTransactions([]);
          setRecentError(error.message || t('overview.recent.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setRecentLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, t]);

  const allocationModeOptions = useMemo(() => ([
    { key: 'TYPE', labelKey: 'overview.allocation.byType' },
    { key: 'CURRENCY', labelKey: 'overview.allocation.byCurrency' },
  ]), []);
  const [allocationMode, setAllocationMode] = useState('TYPE');

  const allocationItems = useMemo(() => {
    const bucket = new Map();
    scopedAssets.forEach((item) => {
      const key = allocationMode === 'CURRENCY'
        ? String(item.currency || 'USD').toUpperCase()
        : String(item.asset_type || 'UNKNOWN').toUpperCase();
      const current = bucket.get(key) || { key, value: 0 };
      bucket.set(key, { ...current, value: current.value + Number(item.market_value_usd || 0) });
    });
    return Array.from(bucket.values())
      .filter((item) => item.value > 0.0001)
      .sort((a, b) => b.value - a.value);
  }, [allocationMode, scopedAssets]);

  const rankingItems = useMemo(() => {
    const items = [...returnRankAssets]
      .filter((item) => Math.abs(Number(item.total_return_rate || 0)) > 0.0001)
      .sort((a, b) => Number(b.total_return_rate || 0) - Number(a.total_return_rate || 0));
    return items.slice(0, isMobileViewport ? 5 : 10);
  }, [isMobileViewport, returnRankAssets]);

  const trendChart = useMemo(() => {
    const width = 760;
    const height = 240;
    const padding = 24;
    const points = Array.isArray(trend.points) ? trend.points : [];
    if (!points.length) {
      return { width, height, padding, path: '', pointIndexByDate: new Map(), yMax: 0, yMin: 0 };
    }
    const values = points.map((p) => Number(p.market_value_usd || 0));
    const yMin = Math.min(...values);
    const yMax = Math.max(...values);
    const path = _buildLinePath(points, width, height, padding);
    const indexMap = new Map(points.map((p, idx) => [String(p.date || ''), idx]));
    return { width, height, padding, path, pointIndexByDate: indexMap, yMax, yMin };
  }, [trend.points]);

  const allocationChart = useMemo(() => {
    const size = 220;
    const cx = size / 2;
    const cy = size / 2;
    const r = 88;
    const colors = ['#9adf7f', '#67c7ff', '#ffb454', '#ff6b6b', '#c89cff', '#6ee7b7', '#fbbf24', '#93c5fd', '#f472b6'];
    const total = allocationItems.reduce((sum, item) => sum + Number(item.value || 0), 0);
    if (total <= 0.0001) {
      return { size, cx, cy, slices: [], total: 0 };
    }
    let cursor = -Math.PI / 2;
    const slices = allocationItems.map((item, index) => {
      const portion = Number(item.value || 0) / total;
      const startAngle = cursor;
      const endAngle = cursor + portion * Math.PI * 2;
      cursor = endAngle;
      return {
        key: item.key,
        value: item.value,
        portion,
        color: colors[index % colors.length],
        path: _describeArc(cx, cy, r, startAngle, endAngle),
      };
    });
    return { size, cx, cy, slices, total };
  }, [allocationItems]);

  const kpiCards = useMemo(() => ([
    {
      key: 'mv',
      title: t('overview.kpi.marketValue', { label: overviewPositionLabel }),
      value: formatNumber(scopedMarketValueUsd, 2),
      tone: '',
    },
    {
      key: 'div',
      title: t('overview.kpi.dividends', { label: overviewPositionLabel }),
      value: formatSigned(scopedDividendUsd),
      tone: Number(scopedDividendUsd || 0) >= 0 ? 'positive' : 'negative',
    },
    {
      key: 'realized',
      title: t('overview.kpi.realized', { label: overviewPositionLabel }),
      value: formatSigned(scopedRealizedProfitUsd),
      tone: Number(scopedRealizedProfitUsd || 0) >= 0 ? 'positive' : 'negative',
    },
    {
      key: 'unrealized',
      title: t('overview.kpi.unrealized', { label: overviewPositionLabel }),
      value: formatSigned(scopedUnrealizedProfitUsd),
      tone: Number(scopedUnrealizedProfitUsd || 0) >= 0 ? 'positive' : 'negative',
    },
    {
      key: 'count',
      title: t('overview.kpi.assetCount', { label: overviewPositionLabel }),
      value: String(scopedAssetCount || 0),
      tone: '',
    },
  ]), [formatNumber, formatSigned, overviewPositionLabel, scopedAssetCount, scopedDividendUsd, scopedMarketValueUsd, scopedRealizedProfitUsd, scopedUnrealizedProfitUsd, t]);

  return (
    <>
      <div className="analyticsPositionFilterRow desktopOnly">
        {positionFilters.map((option) => (
          <button
            key={`overview-${option.key}`}
            className={overviewPositionFilter === option.key ? 'analyticsPositionFilterButton activeAnalyticsPositionFilterButton' : 'analyticsPositionFilterButton'}
            onClick={() => setOverviewPositionFilter(option.key)}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>

      <div className="analyticsPositionStatsGrid">
        {summaryCards.map((item) => (
          <button
            key={`overview-card-${item.key}`}
            className={overviewPositionFilter === item.key ? 'analyticsPositionStatCard activeAnalyticsPositionStatCard' : 'analyticsPositionStatCard'}
            onClick={() => setOverviewPositionFilter(item.key)}
          >
            <span>{t(item.labelKey)}</span>
            <strong>{item.count}</strong>
            <em>{item.detail}</em>
          </button>
        ))}
      </div>

      <div className="overviewKpiGrid">
        {kpiCards.map((card) => (
          <section key={card.key} className={`metricCard overviewKpiCard ${card.tone ? card.tone : ''}`}>
            <span>{card.title}</span>
            <strong className={card.tone}>{card.value}</strong>
          </section>
        ))}
      </div>

      <div className="overviewLayoutGrid">
        <div className="overviewLayoutPrimary">
          <section className="panel overviewTrendPanel">
            <div className="panelHeader compactHeader">
              <span className="panelKicker">Performance Trend</span>
              <h2>{t('overview.trend.title')}</h2>
              <div className="overviewTrendToolbar">
                <button className={trendRangeKey === 'YTD' ? 'chipButton activeChipButton' : 'chipButton'} onClick={() => setTrendRangeKey('YTD')}>{t('overview.trend.rangeYtd')}</button>
                <button className={trendRangeKey === '30D' ? 'chipButton activeChipButton' : 'chipButton'} onClick={() => setTrendRangeKey('30D')}>{t('overview.trend.range30d')}</button>
                <button className={trendRangeKey === '90D' ? 'chipButton activeChipButton' : 'chipButton'} onClick={() => setTrendRangeKey('90D')}>{t('overview.trend.range90d')}</button>
                <button className={trendRangeKey === '1Y' ? 'chipButton activeChipButton' : 'chipButton'} onClick={() => setTrendRangeKey('1Y')}>{t('overview.trend.range1y')}</button>
              </div>
            </div>

            {trendLoading ? <div className="emptyState">{t('overview.trend.loading')}</div> : null}
            {trendError ? <div className="emptyState">{trendError}</div> : null}
            {!trendLoading && !trendError && (!trend.points || !trend.points.length) ? <div className="emptyState">{t('overview.trend.empty')}</div> : null}

            {!trendLoading && !trendError && trend.points && trend.points.length ? (
              <div className="overviewChartWrap">
                <svg
                  className="overviewTrendChart"
                  viewBox={`0 0 ${trendChart.width} ${trendChart.height}`}
                  role="img"
                  aria-label={t('overview.trend.aria')}
                >
                  <path d={trendChart.path} fill="none" stroke="currentColor" strokeWidth="2" />
                  <line x1={trendChart.padding} x2={trendChart.width - trendChart.padding} y1={trendChart.height - trendChart.padding} y2={trendChart.height - trendChart.padding} stroke="currentColor" strokeOpacity="0.15" />
                  {(Array.isArray(trend.dividends) ? trend.dividends : []).map((marker) => {
                    const dateText = String(marker.date || '');
                    const idx = trendChart.pointIndexByDate.get(dateText);
                    const safeIndex = typeof idx === 'number' ? idx : null;
                    if (safeIndex === null) {
                      return null;
                    }
                    const xSpan = Math.max((trend.points.length || 1) - 1, 1);
                    const usableWidth = Math.max(trendChart.width - trendChart.padding * 2, 1);
                    const x = trendChart.padding + (safeIndex / xSpan) * usableWidth;
                    const y = trendChart.height - trendChart.padding;
                    return (
                      <circle
                        key={`div-${dateText}`}
                        cx={x}
                        cy={y}
                        r={4}
                        fill="#ff5b5b"
                      >
                        <title>{`${dateText} · ${t('overview.trend.dividendMarker', { count: marker.count || 0, value: formatSigned(marker.amount_usd || 0) })}`}</title>
                      </circle>
                    );
                  })}
                </svg>
                <div className="overviewTrendMeta">
                  <span>{t('overview.trend.rangeLabel', { start: trend.start_date || trendRange.startDate, end: trend.end_date || trendRange.endDate })}</span>
                  <span>{t('overview.trend.yAxisHint', { max: formatNumber(trendChart.yMax, 2), min: formatNumber(trendChart.yMin, 2) })}</span>
                </div>
              </div>
            ) : null}
          </section>

          <section className="panel overviewAllocationPanel">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">Allocation</span>
            <h2>{t('overview.allocation.title')}</h2>
            <div className="overviewToggleRow">
              {allocationModeOptions.map((item) => (
                <button
                  key={`alloc-${item.key}`}
                  className={allocationMode === item.key ? 'chipButton activeChipButton' : 'chipButton'}
                  onClick={() => setAllocationMode(item.key)}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {allocationChart.total <= 0.0001 ? (
            <div className="emptyState">{t('overview.allocation.empty')}</div>
          ) : (
            <div className="allocationGrid">
              <svg
                className="allocationPie"
                viewBox={`0 0 ${allocationChart.size} ${allocationChart.size}`}
                role="img"
                aria-label={t('overview.allocation.aria')}
              >
                {allocationChart.slices.map((slice) => (
                  <path key={`slice-${slice.key}`} d={slice.path} fill={slice.color} opacity="0.85">
                    <title>{`${slice.key} · ${(slice.portion * 100).toFixed(1)}%`}</title>
                  </path>
                ))}
              </svg>
              <div className="allocationLegend">
                {allocationChart.slices.map((slice) => (
                  <div key={`legend-${slice.key}`} className="allocationLegendRow">
                    <span className="allocationSwatch" style={{ background: slice.color }} />
                    <span className="allocationLabel">{allocationMode === 'TYPE' ? t(`assetType.${slice.key}`) : slice.key}</span>
                    <em>{`${(slice.portion * 100).toFixed(1)}%`}</em>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        </div>

        <div className="overviewLayoutSecondary">
          <aside className="overviewSidebar">
            <section className="panel overviewSidebarPanel">
              <div className="panelHeader compactHeader">
                <span className="panelKicker">Market Pulse</span>
                <h2>{t('overview.marketPulse')}</h2>
              </div>
              <div className="pulseStack">
                {livePulse.map((item) => (
                  <div className="pulseCard" key={item.code}>
                    <div>
                      <strong>{item.label}</strong>
                      <span className="pulseSub">{String(item.currency || '').toUpperCase()}</span>
                    </div>
                    <div className="pulseNumbers">
                      <span>{formatNumber(item.value, getPulseValueDigits(item))}</span>
                      <em className={Number(item.change_percent) < 0 ? 'negative' : 'positive'}>{`${Number(item.change_percent) >= 0 ? '+' : ''}${formatNumber(item.change_percent, 2)}%`}</em>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="panel overviewSidebarPanel">
              <div className="panelHeader compactHeader">
                <span className="panelKicker">Asset Ranking</span>
                <h2>{t('overview.ranking.title')}</h2>
                <p className="workspaceDesc">{t('overview.ranking.desc')}</p>
              </div>
              {rankingItems.length ? (
                <div className="rankingBarList">
                  {rankingItems.map((item) => {
                    const value = Number(item.total_return_rate || 0);
                    const maxValue = Math.max(...rankingItems.map((row) => Math.abs(Number(row.total_return_rate || 0))), 0.0001);
                    const ratio = _clamp(Math.abs(value) / maxValue, 0, 1);
                    return (
                      <button
                        key={`rank-${item.asset_no}`}
                        className="rankingRow"
                        onClick={() => onOpenAsset(item.asset_no)}
                      >
                        <div className="rankingRowHead">
                          <strong>{item.security_name}</strong>
                          <em className={value >= 0 ? 'positive' : 'negative'}>{t('overview.returnRate', { value: formatNumber(value, 2) })}</em>
                        </div>
                        <div className="rankingBarTrack">
                          <div
                            className={value >= 0 ? 'rankingBarFill positiveFill' : 'rankingBarFill negativeFill'}
                            style={{ width: `${(ratio * 100).toFixed(1)}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="emptyState">{t('overview.rankEmpty')}</div>
              )}
            </section>
          </aside>

          <section className="panel overviewRecentPanel">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">Recent Transactions</span>
            <h2>{t('overview.recent.title')}</h2>
          </div>

          {recentLoading ? <div className="emptyState">{t('overview.recent.loading')}</div> : null}
          {recentError ? <div className="emptyState">{recentError}</div> : null}

          {!recentLoading && !recentError && !recentTransactions.length ? (
            <div className="emptyState">{t('overview.recent.empty')}</div>
          ) : null}

          {!recentLoading && !recentError && recentTransactions.length ? (
            <div className={isMobileViewport ? 'recentCardList' : 'tableWrap darkTableWrap'}>
              {isMobileViewport ? (
                recentTransactions.map((tx) => (
                  <div key={`tx-${tx.trade_date}-${tx.asset_no}-${tx.net_amount}`} className="recentTxCard">
                    <div className="recentTxHeader">
                      <strong>{tx.security_name}</strong>
                      <em className={String(tx.type || '').toUpperCase() === 'SELL' ? 'negative' : 'positive'}>{String(tx.type || '').toUpperCase()}</em>
                    </div>
                    <div className="recentTxMeta">
                      <span>{tx.trade_date}</span>
                      <span>{`${tx.currency} · ${t('overview.recent.qty', { value: formatNumber(tx.quantity || 0, 3) })}`}</span>
                      <span>{t('overview.recent.net', { value: formatNumber(tx.net_amount || 0, 2) })}</span>
                    </div>
                  </div>
                ))
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>{t('overview.recent.colDate')}</th>
                      <th>{t('overview.recent.colType')}</th>
                      <th>{t('overview.recent.colAsset')}</th>
                      <th>{t('overview.recent.colQty')}</th>
                      <th>{t('overview.recent.colPrice')}</th>
                      <th>{t('overview.recent.colNet')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((tx) => (
                      <tr key={`tx-${tx.trade_date}-${tx.asset_no}-${tx.net_amount}`}>
                        <td>{tx.trade_date}</td>
                        <td className={String(tx.type || '').toUpperCase() === 'SELL' ? 'negative' : 'positive'}>{String(tx.type || '').toUpperCase()}</td>
                        <td>{tx.security_name}</td>
                        <td>{formatNumber(tx.quantity || 0, 3)}</td>
                        <td>{formatNumber(tx.price || 0, 4)}</td>
                        <td>{formatNumber(tx.net_amount || 0, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </section>
        </div>
      </div>
    </>
  );
};
